'use client';

import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect } from 'react';
import { Burger, Center, Drawer, Group, Menu } from '@mantine/core';
import { useDisclosure, useViewportSize } from '@mantine/hooks';
import { IconChevronDown } from '@tabler/icons-react';
import { useUserStore } from '@/shared/stores/userStore';
import LanguagePicker from '@/app/components/shell/navbar/LanguagePicker';
import UserMenu from '@/app/components/shell/navbar/UserMenu';
import AuthModal from '@/app/components/shell/navbar/authentication/AuthModal';
import { Collapsed } from '@/app/components/shell/navbar/colapsed/Collapsed';
import { links } from '@/app/components/shell/navbar/links';
import { useTranslation } from '@/i18n/client';
import classes from './Header.module.css';

export function Header() {
  const { user, isInitialized } = useUserStore((state) => state);
  const [drawerOpened, { toggle: toggleDrawer, close: closeDrawer }] = useDisclosure(false);
  const { width } = useViewportSize();
  const pathname = usePathname();
  const { t, lng } = useTranslation('links');

  useEffect(
    () => {
      if (drawerOpened) closeDrawer();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pathname],
  );

  useEffect(
    () => {
      if (drawerOpened && width > 990) closeDrawer();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [width],
  );

  useEffect(() => {
    if (typeof window !== 'undefined' && drawerOpened && window.innerWidth > 1000) {
      closeDrawer();
    }
  }, [closeDrawer, drawerOpened]);

  const items = links.map((link) => {
    const menuItems = link.links?.map((item) => (
      <Link
        key={item.link}
        href={item?.noPrefetch ? item.link : `/${lng}${item.link}`}
        style={{ textDecoration: 'none' }}
        prefetch={item?.noPrefetch ? false : 'auto'}
      >
        <Menu.Item>{t(item.label).toUpperCase()}</Menu.Item>
      </Link>
    ));

    if (menuItems) {
      return (
        <Menu key={link.label} trigger='hover' transitionProps={{ exitDuration: 0 }} withinPortal>
          <Menu.Target>
            <a
              href={link.link}
              className={classes.link}
              onClick={(event) => event.preventDefault()}
            >
              <Center>
                <span className={classes.linkLabel}>{t(link.label).toUpperCase()}</span>
                <IconChevronDown size={16} stroke={1.5} className={classes.icon} />
              </Center>
            </a>
          </Menu.Target>
          <Menu.Dropdown>{menuItems}</Menu.Dropdown>
        </Menu>
      );
    }

    return (
      <Link
        key={link.label}
        href={`/${lng}${link.link}`}
        className={classes.link}
        data-active={
          pathname === `/${lng}${link.link}` ||
          ((pathname === '/ru' || pathname === '/en') && link.link === '/') ||
          undefined
        }
      >
        {t(link.label).toUpperCase()}
      </Link>
    );
  });

  return (
    <div className={classes.inner}>
      <Group gap={5} visibleFrom='md'>
        <Group visibleFrom='lg' mr={10}>
          <Link href='/'>
            <Image
              src='/static/images/asura.png'
              width={29}
              height={26}
              alt='logo'
              style={{ marginTop: '4px' }}
            />
          </Link>
        </Group>
        {items}
      </Group>

      <Group visibleFrom='md'>
        <LanguagePicker />
        {isInitialized && <>{user ? <UserMenu user={user} /> : <AuthModal />}</>}
      </Group>

      <Burger opened={drawerOpened} onClick={toggleDrawer} size='sm' hiddenFrom='md' />
      <Group hiddenFrom='md'>
        <LanguagePicker />
      </Group>

      <Drawer
        opened={drawerOpened}
        onClose={closeDrawer}
        padding='md'
        title={t('Navigation')}
        hiddenFrom='md'
        zIndex={1000000}
      >
        <Collapsed />
      </Drawer>
    </div>
  );
}
